import api from './api';

const agencyService = {
  // Get agency dashboard metrics
  getDashboard: async () => {
    try {
      const response = await api.get('/agency/dashboard');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch agency dashboard:', error);
      throw error;
    }
  },

  // Get all agents in the agency
  getAgents: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/agency/agents${queryParams ? '?' + queryParams : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch agency agents:', error);
      throw error;
    }
  },

  // Get agency commissions
  getCommissions: async (params = {}) => {
    try {
      // Filter out undefined values to prevent sending "undefined" strings
      const filteredParams = Object.entries(params)
        .filter(([key, value]) => value !== undefined && value !== null && value !== '')
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
      
      const queryParams = new URLSearchParams(filteredParams).toString();
      const response = await api.get(`/agency/commissions${queryParams ? '?' + queryParams : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch agency commissions:', error);
      throw error;
    }
  },

  // Get agency analytics
  getAnalytics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/agency/analytics${queryParams ? '?' + queryParams : ''}`);
      
      // Return the data property from the response, or the response itself if no data property
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Failed to fetch agency analytics:', error);
      
      // Don't try to access response headers that might be restricted
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      
      throw error;
    }
  },

  // Get agency settings
  getSettings: async () => {
    try {
      const response = await api.get('/agency/settings');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch agency settings:', error);
      throw error;
    }
  },

  // Update agency settings
  updateSettings: async (data) => {
    try {
      const response = await api.put('/agency/settings', data);
      return response.data;
    } catch (error) {
      console.error('Failed to update agency settings:', error);
      throw error;
    }
  },

  // Get pending applications
  getApplications: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/agency/applications${queryParams ? '?' + queryParams : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch agency applications:', error);
      throw error;
    }
  },

  // Get suppliers
  getSuppliers: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/agency/suppliers${queryParams ? '?' + queryParams : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      throw error;
    }
  },

  // Create supplier
  createSupplier: async (data) => {
    try {
      const response = await api.post('/agency/suppliers', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create supplier:', error);
      throw error;
    }
  },

  // Update supplier
  updateSupplier: async (id, data) => {
    try {
      const response = await api.put(`/agency/suppliers/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update supplier:', error);
      throw error;
    }
  },

  // Delete supplier
  deleteSupplier: async (id) => {
    try {
      const response = await api.delete(`/agency/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      throw error;
    }
  },

  // Get agents
  getAgents: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/agency/agents${queryParams ? '?' + queryParams : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      throw error;
    }
  },

  // Update agent
  updateAgent: async (id, data) => {
    try {
      const response = await api.put(`/agency/agents/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update agent:', error);
      throw error;
    }
  },

  // Approve application
  approveApplication: async (id) => {
    try {
      const response = await api.put(`/agency/applications/${id}/approve`);
      return response.data;
    } catch (error) {
      console.error('Failed to approve application:', error);
      throw error;
    }
  },

  // Deny application
  denyApplication: async (id, reason) => {
    try {
      const response = await api.put(`/agency/applications/${id}/deny`, { reason });
      return response.data;
    } catch (error) {
      console.error('Failed to deny application:', error);
      throw error;
    }
  },

  // Search agents by location
  searchAgentsByLocation: async (filters = {}) => {
    try {
      // Filter out undefined values to prevent sending "undefined" strings
      const filteredParams = Object.entries(filters)
        .filter(([key, value]) => value !== undefined && value !== null && value !== '')
        .reduce((obj, [key, value]) => {
          // Convert arrays to comma-separated strings for URL parameters
          const paramValue = Array.isArray(value) ? value.join(',') : value;
          return { ...obj, [key]: paramValue };
        }, {});
      
      const queryParams = new URLSearchParams(filteredParams).toString();
      const response = await api.get(`/agency/agents/search${queryParams ? '?' + queryParams : ''}`);
      return response.data;
    } catch (error) {
      console.error('Failed to search agents by location:', error);
      throw error;
    }
  },

  // Get unique states for agent filtering
  getAgentStates: async () => {
    try {
      const response = await api.get('/agency/agents/states');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch agent states:', error);
      throw error;
    }
  },

  // Get unique countries for agent filtering
  getAgentCountries: async () => {
    try {
      const response = await api.get('/agency/agents/countries');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch agent countries:', error);
      throw error;
    }
  },

  // Get denied applications
  getDeniedApplications: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      console.log('🔥 FRONTEND: Calling denied applications API with params:', params);
      const response = await api.get(`/agency/applications/denied${queryParams ? '?' + queryParams : ''}`);
      console.log('🔥 FRONTEND: Denied applications API response:', response);
      console.log('🔥 FRONTEND: Response data:', response.data);
      console.log('🔥 FRONTEND: Response data.data:', response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch denied applications:', error);
      console.error('Error details:', error.response?.data);
      throw error;
    }
  },

  // Approve denied application
  approveDeniedApplication: async (id) => {
    try {
      const response = await api.put(`/agency/applications/denied/${id}/approve`);
      return response.data;
    } catch (error) {
      console.error('Failed to approve denied application:', error);
      throw error;
    }
  },

  // Delete denied application
  deleteDeniedApplication: async (id) => {
    try {
      const response = await api.delete(`/agency/applications/denied/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete denied application:', error);
      throw error;
    }
  },

  // Get suppliers
  getSuppliers: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await api.get(`/agency/suppliers${queryParams ? '?' + queryParams : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      throw error;
    }
  },

  // Get agents benefits data (commission submission status)
  getBenefitsData: async () => {
    try {
      const response = await api.get('/agency/benefits');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch benefits data:', error);
      throw error;
    }
  },

  // Update agent email address
  updateAgentEmail: async (id, email) => {
    try {
      const response = await api.put(`/agency/agents/${id}/email`, { email });
      return response.data;
    } catch (error) {
      console.error('Failed to update agent email:', error);
      throw error;
    }
  },

  // Send password reset email to agent
  sendPasswordReset: async (id) => {
    try {
      const response = await api.post(`/agency/agents/${id}/password-reset`);
      return response.data;
    } catch (error) {
      console.error('Failed to send password reset:', error);
      throw error;
    }
  },
};

export default agencyService;
